import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { hoyEnMerida, hoyEnMeridaISO } from '@erp/shared';
import { es } from 'date-fns/locale';

// 🕐 La hora de negocio NO se calcula aquí: el dueño único es `packages/shared/types/fechas.ts` (`hoyEnMerida`, `hoyEnMeridaISO`…, vía '@erp/shared').
// Aquí sólo se FORMATEA lo que ese reloj entrega.
function toMerida(date: string | Date): Date {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return new Date();
  return hoyEnMerida(d);   // un INSTANTE visto en hora de Mérida
}

/**
 * Formatea una fecha a "DD/MM/YYYY" en hora de Mérida.
 * Ejemplo: "15/03/2024"
 */
export function formatearFecha(date: string | Date): string {
  try {
    const zonedDate = toMerida(date);
    return format(zonedDate, 'dd/MM/yyyy');
  } catch {
    return '—';
  }
}

/**
 * Formatea una fecha DE CALENDARIO ('AAAA-MM-DD') sin cruzar husos horarios.
 *
 * 🔴 Por qué existe (MEDIDO 14-ago, I-106): `formatearFecha` mete la fecha por `parseISO` y
 * la mueve a Mérida. Con un instante eso es correcto; con una fecha de calendario —una
 * columna DATE, que no tiene hora— la corre un día.
 *
 * ⚠️ CORREGIDO EL 17-ago: esta nota decía que sólo pasaba «si el navegador NO está en
 * Mérida», y eso es FALSO para el caso que de verdad ocurre — leerlo así hace pensar que
 * aquí estamos a salvo, y no lo estamos. Drizzle lee estas columnas en modo `date`
 * (`tenant-schema.ts:1746`), así que el backend NO manda '2026-10-31': manda el INSTANTE
 * '2026-10-31T00:00:00.000Z'. Medido contra el backend real ese día:
 *
 *   '2026-10-31T00:00:00.000Z' · navegador en MÉRIDA → 30/10/2026 ❌  ← el caso real
 *   '2026-10-31T00:00:00.000Z' · navegador en UTC    → 30/10/2026 ❌
 *   '2026-10-31' (string pelón)  · navegador en Mérida → 31/10/2026 ✅
 *   '2026-10-31' (string pelón)  · navegador en Madrid → 30/10/2026 ❌
 *
 * O sea: falla SIEMPRE cuando la fecha viaja como instante, que es como viaja el plan de
 * pagos. Por eso se vio en pantalla estando en Mérida.
 *
 * Un vencimiento, una firma o la fecha que armó el calendario NO son instantes: son días.
 * Se pintan tal cual vienen.
 */
export function formatearFechaDeCalendario(fecha: string | null | undefined): string {
  const s = String(fecha ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [anio, mes, dia] = s.split('-');
  return `${dia}/${mes}/${anio}`;
}

/**
 * Días de calendario entre HOY (Mérida) y una fecha de vencimiento — negativo si ya
 * venció, 0 si vence hoy, positivo si falta. `null` si `fecha` no es una fecha de
 * calendario válida.
 *
 * 🔴 Por qué existe (huso Mérida, ver diasHastaVencimiento.test.ts): comparar
 * `new Date(fechaDeCalendario) < new Date()` compara un INSTANTE (medianoche UTC de la
 * fecha) contra OTRO INSTANTE (el reloj real). Entre las 18:00 y las 23:59 hora Mérida
 * el reloj real ya cayó en el día UTC siguiente mientras en Mérida sigue siendo hoy —
 * algo que vence HOY se marcaba vencido antes de tiempo. Esta función compara
 * CALENDARIO contra CALENDARIO (vía `hoyEnMeridaISO()`), igual que el backend hace con
 * `DATEDIFF(hoyEnMeridaISO(), fecha_vencimiento)` — nunca instante contra instante.
 */
export function diasHastaVencimiento(fecha: string | null | undefined, ahora: Date = new Date()): number | null {
  const s = String(fecha ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [av, mv, dv] = s.split('-').map(Number);
  const hoy = hoyEnMeridaISO(ahora);
  const [ah, mh, dh] = hoy.split('-').map(Number);
  const vencimientoUTC = Date.UTC(av, mv - 1, dv);
  const hoyUTC = Date.UTC(ah, mh - 1, dh);
  return Math.round((vencimientoUTC - hoyUTC) / 86_400_000);
}

/**
 * Convierte una fecha DE CALENDARIO en un `Date` **local**, para las pantallas que necesitan
 * darle su propio formato (`toLocaleDateString` con nombre de mes, día de la semana, etc.).
 *
 * 🔴 Por qué existe (MEDIDO 17-ago, I-111): `new Date('2026-10-31')` y
 * `new Date('2026-10-31T00:00:00.000Z')` producen medianoche **UTC**, y cualquier formateo local
 * en Mérida (UTC-6) los corre al día anterior. Dos pantallas lo estaban cometiendo por fuera de
 * `formatearFecha`, así que ni el barrido ni el candado las veían:
 *
 *   `new Date('2026-10-31T00:00:00.000Z').toLocaleDateString('es-MX', …)` → **30 oct 26** ❌
 *   `new Date('2026-08-01').toLocaleDateString('es-MX', {month:'long'})`  → **julio de 2026** ❌
 *      ↑ ése no se equivocaba de día: se equivocaba de MES, en la pantalla de cartera.
 *
 * Construyendo el `Date` con los tres números (que el constructor interpreta en hora LOCAL) la
 * fecha se queda donde está, y la pantalla conserva el formato que ya tenía.
 */
export function fechaDeCalendarioComoDateLocal(fecha: string | null | undefined): Date | null {
  const s = String(fecha ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [anio, mes, dia] = s.split('-').map(Number);
  return new Date(anio, mes - 1, dia);
}

/**
 * Formatea una fecha a "DD/MM/YYYY HH:mm" en hora de Mérida.
 * Ejemplo: "15/03/2024 14:30"
 */
export function formatearFechaHora(date: string | Date): string {
  try {
    const zonedDate = toMerida(date);
    return format(zonedDate, 'dd/MM/yyyy HH:mm');
  } catch {
    return '—';
  }
}

/** Meses cortos en minúscula, sin punto — "2 sep, 03:40" (Carril C, panel del pago). */
export const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * "2 sep, 03:40" en hora de Mérida — el formato que pide el diseño (`casa-pago-panel.jsx:44`)
 * para la fecha de cada firma dentro del panel del pago.
 *
 * 🔴 NO sustituye a `formatearFechaHora` ("dd/MM/yyyy HH:mm"): esa es la fecha de AUDITORÍA
 *    que usa el resto del ERP, y cambiarla le cambiaría el formato a pantallas que no pidieron
 *    este rediseño. Este formateador es sólo para las firmas del panel de Carril C.
 * 🔒 Escrito a mano (sin `date-fns` locale) para que el mes salga EXACTO como lo pide el
 *    diseño ("sep", no "sept." que da el locale `es` de `date-fns` para septiembre).
 */
export function formatearFirma(date: string | Date): string {
  try {
    // `toMerida()` cae a `new Date()` en silencio ante una fecha inválida (mismo patrón que
    // el resto de este archivo) — aquí SÍ se valida antes, para no fingir una hora que nunca
    // pasó cuando el historial de firmas manda un valor roto.
    const crudo = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(crudo)) return '—';
    const zonedDate = toMerida(date);
    const dia = zonedDate.getDate();
    const mes = MES_CORTO[zonedDate.getMonth()];
    const hh = String(zonedDate.getHours()).padStart(2, '0');
    const mm = String(zonedDate.getMinutes()).padStart(2, '0');
    return `${dia} ${mes}, ${hh}:${mm}`;
  } catch {
    return '—';
  }
}

/**
 * Formatea una fecha como texto relativo.
 * Ejemplo: "hace 3 horas", "ayer", "hace 5 días"
 */
export function formatearRelativo(date: string | Date): string {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(d)) return '—';
    return formatDistanceToNow(d, { addSuffix: true, locale: es });
  } catch {
    return '—';
  }
}

/**
 * Formatea un número como moneda MXN, con sus 2 decimales — el `mxn` del diseño
 * (`data-finanzas.jsx:40`). Ejemplo: "$1,234.56 MXN" o "$1,234.56".
 *
 * 🔒 D-2026-09-22-J (BUG-2210): los montos tienen DOS formas y sólo dos —
 *   · ésta, 2 decimales: tablas y montos exactos;
 *   · `mxn()` de `components/M02/casaTema.ts`, sin centavos y sin abreviar: tarjetas y KPIs grandes.
 * Todo otro formateador de dinero llama a uno de los dos. `{ signo: false }` es la misma forma sin
 * el `$` (el `mxn(n, { signo:false })` del diseño: columnas de Debe/Haber).
 */
export function formatearMonto(
  amount: number | string | null | undefined,
  incluirSufijo = false,
  opts: { signo?: boolean } = {},
): string {
  try {
    const n = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
    const formatted = new Intl.NumberFormat('es-MX', {
      ...(opts.signo === false ? {} : { style: 'currency', currency: 'MXN' }),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(isNaN(n) ? 0 : n);

    return incluirSufijo ? `${formatted} MXN` : formatted;
  } catch {
    return opts.signo === false ? '0.00' : '$0.00';
  }
}

/**
 * Formatea un porcentaje.
 * Ejemplo: "12.5%"
 *
 * 🔒 BUG-2402: acepta `string` porque un `DECIMAL` de MySQL (Drizzle/mysql2) llega como
 * string, no como number — el mismo caso que ya cubre `formatearMonto` arriba. Un
 * `.toFixed()` suelto sobre ese valor revienta con "toFixed is not a function".
 */
export function formatearPorcentaje(value: number | string | null | undefined, decimales = 1): string {
  // Sin número, el sistema no inventa un 0: dice que no hay dato (CLAUDE.md §31 regla 4).
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(decimales)}%`;
}

/** Tamaño de archivo legible: "512 B" · "3.2 KB" · "1.4 MB". */
export function formatearBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * BUG-1875 — vivía duplicada carácter por carácter en TareaListView.tsx y
 * TareaDetailPanel.tsx (M08, tareas). Días de calendario entre hoy y la fecha dada
 * (negativo = ya venció). `9999` como centinela de "sin fecha" para que ordene al
 * final en las listas que ya dependían de ese valor.
 *
 * BUG-1883 — la duplicada hacía `new Date(iso) < new Date()` (o el equivalente con
 * `setHours(0,0,0,0)` sobre un `new Date(iso)` construido de un string `YYYY-MM-DD`,
 * que JS interpreta como MEDIANOCHE UTC): compara un INSTANTE contra el reloj real,
 * y entre 18:00 y 23:59 hora Mérida una tarea que vence HOY ya se leía vencida. Es
 * la MISMA clase de defecto que `diasHastaVencimiento()` (abajo) ya resolvía para
 * M02/M05 — se unifica aquí en vez de mantener una segunda cuenta de días paralela:
 * `diasPara` es ahora un envoltorio delgado que sólo agrega el centinela 9999 que
 * sus 2 consumidores de M08 ya esperaban.
 */
export function diasPara(iso: string | null): number {
  const dias = diasHastaVencimiento(iso);
  return dias === null ? 9999 : dias;
}
